import type { ScheduleTherapist } from "../types/schedule";

export interface TimeSlot {
  time: string;
  available: boolean;
}

export interface BusySlot {
  start: string;
  end: string;
}

export const SLOTS_CACHE_TTL_MS = 5 * 60 * 1000;

export function formatDateKey(date: Date): string {
  return date.toISOString().split("T")[0];
}

export function generateTimeSlots(start: string, end: string, interval: number): string[] {
  const slots: string[] = [];
  const currentTime = new Date(`1970-01-01T${start}:00`);
  const endTime = new Date(`1970-01-01T${end}:00`);

  while (currentTime < endTime) {
    slots.push(currentTime.toTimeString().substring(0, 5));
    currentTime.setMinutes(currentTime.getMinutes() + interval);
  }
  return slots;
}

/** Horários possíveis pela jornada do terapeuta (sem filtrar ocupados). */
export function computePreviewSlots(therapist: ScheduleTherapist, date: Date): TimeSlot[] {
  const dayOfWeek = date.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
  const workHours = therapist.horariosDisponiveis?.[dayOfWeek];
  const sessionDuration = therapist.sessionDuration || 60;
  if (!workHours?.length) return [];

  const dateKey = formatDateKey(date);
  const now = new Date();
  let allPossibleSlots: string[] = [];
  workHours.forEach((period) => {
    allPossibleSlots = [...allPossibleSlots, ...generateTimeSlots(period.inicio, period.fim, sessionDuration)];
  });

  return allPossibleSlots
    .filter((slotTime) => {
      const slotStart = new Date(`${dateKey}T${slotTime}:00`);
      return slotStart >= now;
    })
    .map((time) => ({ time, available: true }));
}

export function computeAvailableSlots(
  therapist: ScheduleTherapist,
  date: Date,
  busySlots: BusySlot[]
): TimeSlot[] {
  const dayOfWeek = date.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
  const workHours = therapist.horariosDisponiveis?.[dayOfWeek];
  const sessionDuration = therapist.sessionDuration || 60;
  if (!workHours?.length) return [];

  const dateKey = formatDateKey(date);
  const now = new Date();
  let allPossibleSlots: string[] = [];
  workHours.forEach((period) => {
    allPossibleSlots = [...allPossibleSlots, ...generateTimeSlots(period.inicio, period.fim, sessionDuration)];
  });

  const finalSlots = allPossibleSlots.filter((slotTime) => {
    const slotStart = new Date(`${dateKey}T${slotTime}:00`);
    const slotEnd = new Date(slotStart.getTime() + sessionDuration * 60000);
    if (slotStart < now) return false;
    return !busySlots.some((busy) => {
      const busyStart = new Date(busy.start);
      const busyEnd = new Date(busy.end);
      return slotStart < busyEnd && slotEnd > busyStart;
    });
  });

  return finalSlots.map((time) => ({ time, available: true }));
}

export function getUpcomingWorkingDates(therapist: ScheduleTherapist, from: Date, limit = 5): Date[] {
  const dates: Date[] = [];
  const cursor = new Date(from);
  cursor.setHours(0, 0, 0, 0);

  for (let i = 0; i < 45 && dates.length < limit; i++) {
    const dayOfWeek = cursor.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
    const workHours = therapist.horariosDisponiveis?.[dayOfWeek];
    if (workHours?.length) dates.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
}

export function readSlotsCache(therapistId: string, dateKey: string): { fetchedAt: number; busySlots: BusySlot[] } | null {
  try {
    const raw = localStorage.getItem(`schedule_slots_${therapistId}_${dateKey}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { fetchedAt: number; busySlots: BusySlot[] };
    if (!parsed?.fetchedAt || !Array.isArray(parsed.busySlots)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeSlotsCache(therapistId: string, dateKey: string, busySlots: BusySlot[]): void {
  try {
    localStorage.setItem(
      `schedule_slots_${therapistId}_${dateKey}`,
      JSON.stringify({ fetchedAt: Date.now(), busySlots })
    );
  } catch {
    // quota ou modo privado — ignora
  }
}
